import React, { useEffect } from 'react'
import { useTabManager } from '../../hooks/useTabManager'
import { useAuth } from '../../contexts/AuthContext'
import { TabBar } from './TabBar'
import { WindowContainer } from './WindowContainer'
import { GirasolesViewWindow } from '../../windows/GirasolesView'
import { STLInView } from '../../windows/STLInView'
import { Marketplace } from '../../windows/Marketplace'
import ProfileView from '../../windows/ProfileView'
import { AIAssistantWindow } from '../../windows/AIAssistant/AIAssistantWindow'
import { TabProvider, TabContextType } from '../../contexts/TabContext'
import { PageContextProvider, usePageContext } from '../../contexts/PageContextContext'
import { STLLoadResult } from '../../utils/stlLoader'
import type { MarketplaceLaunchContext } from '../../types/windows'
import './TabManager.css'

export const TabManager: React.FC<{ deepLinkProjectId?: string | null }> = ({ deepLinkProjectId }) => {
  const { isAdmin } = useAuth()
  const tabManager = useTabManager(isAdmin)

  const handleAddGirasolesView = (stlData?: STLLoadResult, projectId?: string) => {
    // Always allow multiple Girasoles View tabs
    const viewNumber = tabManager.tabs.filter(t => t.type === 'girasoles_view').length + 1
    tabManager.addTab({
      title: `Girasoles View ${viewNumber}`,
      type: 'girasoles_view',
      closable: true,
      component: GirasolesViewWindow,
      props: { stlData, projectId }
    })
  }

  const handleAddSTLInView = (projectName: string, customerName: string, projectId: string) => {
    // Use focusOrCreateTab to prevent duplicates for same project
    tabManager.focusOrCreateTab({
      title: `STL_IN - ${projectName}`,
      type: 'stl_in',
      closable: true,
      component: STLInView,
      props: {
        projectId,
        customerName,
        projectName
      }
    })
  }

  const handleAddSTLOutView = (
    projectName: string,
    customerName: string,
    projectId: string,
    refreshKey?: string
  ) => {
    // If the STL_OUT tab already exists, update its props so STLInView can reload.
    const existingTab = tabManager.findExistingTab('stl_out', projectId)
    if (existingTab) {
      tabManager.updateTab(existingTab.id, {
        props: {
          ...(existingTab.props || {}),
          projectId,
          customerName,
          projectName,
          fileRole: 'stl_out',
          refreshKey
        }
      })
      tabManager.switchTab(existingTab.id)
      return
    }

    tabManager.focusOrCreateTab({
      title: `STL_OUT - ${projectName}`,
      type: 'stl_out',
      closable: true,
      component: STLInView, // TODO: Create STLOutView component
      props: {
        projectId,
        customerName,
        projectName,
        fileRole: 'stl_out',
        refreshKey
      }
    })
  }

  const handleAddMarketplace = (launchContext?: MarketplaceLaunchContext) => {
    const existingMarketplaceTab = tabManager.findExistingTab('marketplace')

    if (existingMarketplaceTab) {
      if (launchContext) {
        tabManager.updateTab(existingMarketplaceTab.id, {
          props: {
            ...(existingMarketplaceTab.props || {}),
            launchContext
          }
        })
      }

      tabManager.switchTab(existingMarketplaceTab.id)
      return
    }

    tabManager.addTab({
      title: 'Marketplace',
      type: 'marketplace',
      closable: true,
      component: Marketplace,
      props: launchContext ? { launchContext } : {}
    })
  }

  const handleAddProfileView = () => {
    const existingTab = tabManager.findExistingTab('profile')
    if (existingTab) {
      tabManager.switchTab(existingTab.id)
      return
    }

    tabManager.addTab({
      title: 'Profile',
      type: 'profile',
      closable: true,
      component: ProfileView,
    })
  }

  const handleAddAIAssistant = () => {
    const existingTab = tabManager.findExistingTab('ai_assistant')
    if (existingTab) {
      tabManager.switchTab(existingTab.id)
      return
    }

    tabManager.addTab({
      title: 'AI Assistant',
      type: 'ai_assistant',
      closable: true,
      component: AIAssistantWindow,
    })
  }

  const tabActions = {
    addGirasolesView: handleAddGirasolesView,
    addSTLInView: handleAddSTLInView,
    addSTLOutView: handleAddSTLOutView,
    addMarketplace: handleAddMarketplace,
    addProfileView: handleAddProfileView,
    addAIAssistant: handleAddAIAssistant,
    listTabs: () => tabManager.tabs,
    switchTab: (tabId: string) => tabManager.switchTab(tabId),
  }

  // Deep link: auto-open Girasoles View for a project.
  // Intentional one-shot — only run when the deep-link id changes, so we do NOT
  // include handleAddGirasolesView (recreated every render) in the deps.
  useEffect(() => {
    if (deepLinkProjectId) {
      handleAddGirasolesView(undefined, deepLinkProjectId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkProjectId])

  return (
    <PageContextProvider>
      <TabManagerInner tabManager={tabManager} tabActions={tabActions} />
    </PageContextProvider>
  )
}

/** Inner component that has access to PageContextProvider */
const TabManagerInner: React.FC<{
  tabManager: ReturnType<typeof useTabManager>
  tabActions: TabContextType
}> = ({ tabManager, tabActions }) => {
  const { setActivePage } = usePageContext()

  // Push active tab info to PageContext whenever the active tab changes
  useEffect(() => {
    const activeTab = tabManager.tabs.find(t => t.id === tabManager.activeTabId)
    if (activeTab) {
      const tabProps = (activeTab.props || {}) as Record<string, unknown>
      setActivePage({
        window_type: activeTab.type,
        tab_title: activeTab.title,
        project_name: (tabProps.projectName as string) || (tabProps.projectId as string) || undefined,
      })
    } else {
      setActivePage(null)
    }
  }, [tabManager.activeTabId, tabManager.tabs, setActivePage])

  return (
    <TabProvider tabActions={tabActions}>
      <div className="tab-manager">
        {/* Main Window Content Area — render all tabs, show only active */}
        <div className="window-content">
          {tabManager.tabs.map(tab => (
            <div
              key={tab.id}
              style={{ display: tab.id === tabManager.activeTabId ? 'contents' : 'none' }}
            >
              <WindowContainer
                tab={tab}
                onClose={() => tabManager.removeTab(tab.id)}
              />
            </div>
          ))}
        </div>

        {/* Bottom Tab Bar */}
        <TabBar
          tabs={tabManager.tabs}
          activeTabId={tabManager.activeTabId}
          onTabClick={tabManager.switchTab}
          onTabClose={tabManager.removeTab}
        />
      </div>
    </TabProvider>
  )
}
